import { useState, useRef } from "react";
import type { Field } from "../types/recommendation";
/* Same reason as `SourceDrawer` — see the note there. The chip is L1 of the disclosure pattern and
   is rendered inside tables, cards and sentences on every screen in the app, so a frozen light-mode
   tier colour is the defect at its widest. */
import { themeTierStyles as tierStyles, themeVar as colors } from "../design-tokens";
import { SourceDrawer } from "./SourceDrawer";

/**
 * The "Show Your Work" citation chip — L1 of the three-level disclosure
 * pattern (ValuBrief_Design_and_Roadmap.md §2). Build first, reuse
 * everywhere a figure or legal cite appears (project standing instruction).
 *
 * L1 (this component, always visible): the figure + a small trailing "ⓘ" +
 * a one-word tier status. Keyboard-focusable, opens the drawer on click OR
 * Enter/Space.
 *
 * This component is a PURE RENDERER — it displays `field.tier`/`field.display`
 * exactly as the server computed them. It never infers, formats-as-computed,
 * or recalculates a tier from other fields; that classification is the
 * engine's job (ADR-0003 §4: "React never computes one").
 */
export function ProvenanceChip({ field, size = "inline" }: {
  field: Field;
  /**
   * `inline` is the original: the figure and its chip on one line, inside a sentence or a table
   * cell. `figure` stacks them — the number at reading size with its tags underneath — for a card
   * or a headline where the figure is the thing being looked at rather than mentioned.
   *
   * Nothing about the provenance changes with the size. Both render the same tags, from the same
   * field, and open the same drawer.
   */
  size?: "inline" | "figure";
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const style = tierStyles[field.tier];
  const stacked = size === "figure";

  return (
    <span style={stacked
      ? { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }
      : { display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          fontFamily: "var(--font-mono, 'JetBrains Mono', ui-monospace, monospace)",
          fontVariantNumeric: "tabular-nums",
          color: colors.textPrimary,
          // A figure that shrinks a little rather than wrapping. "2,380 SF" at 21px needs ~100px,
          // which is more than a third of a 342px card on a phone; at 17px it fits on one line.
          // Measured at 390 and 1512 in Chromium, and it stops growing at 21 on both.
          ...(stacked
            ? { fontSize: "clamp(17px, 1.35vw + 12px, 21px)", fontWeight: 600,
                letterSpacing: "-0.01em", lineHeight: 1.1, whiteSpace: "nowrap" as const }
            : null),
        }}
      >
        {field.display}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Show source for ${field.label}: ${style.label}. ${field.display}.`}
        onClick={() => setOpen(true)}
        // UI-129: this chip measures 20.8px tall on a 390px phone — the affordance the entire
        // product turns on, at under half the repo's 44x44 minimum. `data-vb-hit` grows the TARGET
        // in `app.css` and leaves the painted box alone, so it still reads as a small inline tag.
        data-vb-hit="44"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          border: "none",
          borderRadius: 4,
          padding: "2px 6px",
          fontSize: 12,
          lineHeight: 1.4,
          cursor: "pointer",
          color: style.fg,
          background: style.bg,
        }}
      >
        <TierIcon icon={style.icon} />
        {style.label}
        <span aria-hidden="true">ⓘ</span>
      </button>
      {/*
        THE SECOND TAG, AND ONLY WHEN THERE IS ONE (2026-08-29).
        The provenance model is two tags: a SOURCE tag always, and a STATE tag only when the value
        was transformed. `Field.state` has carried that state since the contract was written and no
        chip has ever rendered it — so a figure this app DIVIDED and a figure it read straight off a
        record were indistinguishable at a glance, and the distinction only appeared once the reader
        opened the drawer. That is exactly backwards: the transformation is the thing a skeptic needs
        to see without asking.

        Quiet on purpose. It is a statement about the value, not a warning about it, and the tier
        chip beside it already carries the colour. Text does the work; the colour is neutral.
      */}
      {field.state && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            borderRadius: 4,
            padding: "2px 6px",
            fontSize: 12,
            lineHeight: 1.4,
            color: colors.textSecondary,
            background: colors.neutralChip,
            border: `1px solid ${colors.border}`,
          }}
        >
          {field.state}
        </span>
      )}
      </span>
      {open && (
        <SourceDrawer
          field={field}
          onClose={() => {
            setOpen(false);
            // Restore focus to the trigger — the documented gap (UI-06) in the
            // legacy drawer was losing focus back to nowhere on close.
            triggerRef.current?.focus();
          }}
        />
      )}
    </span>
  );
}

/** Icon always pairs with the text label above — color never carries meaning
 * alone (design-tokens.ts / roadmap doc rule). Minimal inline SVGs, no icon
 * font/CDN dependency. */
function TierIcon({ icon }: { icon: "check" | "triangle" | "dash" | "info" }) {
  const common = { width: 12, height: 12, "aria-hidden": true as const, focusable: false as const };
  switch (icon) {
    case "check":
      return (
        <svg {...common} viewBox="0 0 16 16" fill="none">
          <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "triangle":
      return (
        <svg {...common} viewBox="0 0 16 16" fill="none">
          <path d="M8 2l7 12H1L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8 6.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case "dash":
      return (
        <svg {...common} viewBox="0 0 16 16" fill="none">
          <path d="M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "info":
    default:
      return (
        <svg {...common} viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <circle cx="8" cy="5" r="0.9" fill="currentColor" />
        </svg>
      );
  }
}
